import { useToast } from '@airship-platform/airship-patterns';
import {
  DescriptionEditable,
  ManageProductOptions,
  ManageProductSection,
  OrganizationSOW,
  useOrganizationSOWs,
} from '@airship-platform/airship-proposal';
import {
  EmptyState,
  FlexBox,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { BackButton } from '@airship-platform/airship-patterns';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { useAppointmentProposalOptionSOWs } from '../../data-access/appointmentProposalOptionSows';
import ROUTE_PATHS from '../../routes/paths';

const AddSOWPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { sowId } = useParams<{ sowId: string }>();
  const { sows: availableSOWs } = useOrganizationSOWs();
  const { options, currentOption } = useAppointmentProposalOptions();
  const {
    createManyAppointmentProposalOptionSOWs,
    getNextSortOrder,
    isLoadingSOWIds,
  } = useAppointmentProposalOptionSOWs();

  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(
    currentOption ? [currentOption.appointmentProposalOptionId] : []
  );

  const selectedSOWs = availableSOWs
    .filter((sow) => sow.sowId === sowId)
    .map((sow) => ({
      ...sow,
      description: sow.summary,
    }));

  const [sows, setSows] = useState<OrganizationSOW[]>(selectedSOWs);

  if (!sows.length || !currentOption) {
    return (
      <EmptyState
        title="Statement of work not found"
        subtitle="The requested statement of work could not be located or is unavailable for this appointment."
      />
    );
  }

  const handleSubmit = () => {
    const selectedSOWs = sows.filter((sow) => sow.sowId === sowId);

    const payloads = selectedOptionIds.flatMap((optionId) =>
      selectedSOWs.map((sow, idx) => ({
        appointmentProposalOptionId: optionId,
        sow: {
          sowId: sow.sowId,
          title: sow.title,
          summary: sow.summary,
          shortDescription: sow.shortDescription,
          jobId: currentOption.jobId,
          sortOrder: getNextSortOrder(idx),
        },
      }))
    );

    createManyAppointmentProposalOptionSOWs(
      currentOption.appointmentId,
      currentOption.appointmentProposalId,
      payloads
    ).then(() => {
      showToast('Statement of work added', 'success');
      navigate(
        ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
          appointmentId: currentOption.appointmentId,
          appointmentProposalId: currentOption.appointmentProposalId,
          appointmentProposalOptionId:
            currentOption.appointmentProposalOptionId,
        })
      );
    });
  };

  const handleUpdate =
    (sowId: string) => (updates: Partial<OrganizationSOW>) => {
      setSows((prev) =>
        prev.map((sow) => (sow.sowId === sowId ? { ...sow, ...updates } : sow))
      );
    };

  return (
    <FlexBox flexDirection={'column'} gap={2} p={2}>
      <Typography variant="h4">Add Statement of Work</Typography>
      {sows.map((sow) => (
        <ManageProductSection
          key={sow.sowId}
          product={{ name: sow.title, images: [] }}
        >
          <Divider />
          <ManageProductOptions
            options={options}
            selectedOptionIds={selectedOptionIds}
            setSelectedOptionIds={setSelectedOptionIds}
            isLoading={isLoadingSOWIds.includes(sow.sowId)}
          />
          <Divider />
          <FlexBox gap={2} justifyContent={'space-between'}>
            <BackButton
              color="softGold"
              size={'large'}
              sx={{ minWidth: '8rem' }}
              defaultPath="../"
            >
              Cancel
            </BackButton>
            <LoadingButton
              onClick={handleSubmit}
              loading={false}
              size={'large'}
              disabled={false}
              sx={{ minWidth: '8rem' }}
            >
              Add
            </LoadingButton>
          </FlexBox>
          <Divider />
          <DescriptionEditable
            limit={5000}
            title="Description"
            description={sow.summary}
            onUpdateDescription={(newSummary) =>
              handleUpdate(sow.sowId)({ summary: newSummary })
            }
            isEditable
          />
        </ManageProductSection>
      ))}
    </FlexBox>
  );
};

export default AddSOWPage;
